/* =============================================================================
   CORE / FOOD-COST — Wareneinsatz / Food-Cost (S158 ERP Phase B)
   =============================================================================
   PURE JS-Spiegel der SQL-RPCs `ingredient_cost_per_base` / `menu_item_cost` /
   `compose_cost` (Migration phase_117). Identische Formel + Rundung, damit die UI
   (Konfigurator/Editor-Live, Zutaten-€/Einheit, Food-Cost-Report) ohne Roundtrip
   rechnen kann. Single-Source des Algorithmus — bei Änderung BEIDE pflegen.

   Formel:
     cost_per_base = (purchase_price / purchase_qty) / (yield_cleaning * yield_cooking)
     qty_base      = base/NULL → quantity ; benannte Einheit → quantity × grams_or_ml
                     (KEINE Stück→Gramm-Stufe — anders als qtyToBase für Nährwerte!)
     line_cost     = qty_base × cost_per_base
     goods_cost    = Σ line_cost über nicht-optionale, nicht abgewählte Zeilen mit Zutat
   Fehlt Einkauf (≤0) oder Ausbeute (≤0) oder unbekannte Einheit → Zeile = missing_data.
============================================================================ */

function _round(x, n) {
  if (x == null || !isFinite(x)) return 0;
  const f = Math.pow(10, n);
  return Math.round((x + Number.EPSILON) * f) / f;
}

/**
 * €/Basiseinheit einer Zutat. Spiegel von `ingredient_cost_per_base`.
 * yield_* default 1.0 → ohne Faktoren = reiner Einkaufspreis.
 *
 * S203 Cut B (bewusst): Food-Cost/Rezept-Kalkulation bleibt auf dem AKTUELLEN
 * Zutaten-Listenpreis (purchase_price/purchase_qty) — sie ist zukunftsgerichtet
 * („was kostet dieses Gericht, wenn ich es heute koche"). Das ist NICHT die
 * Bestandsbewertung: der Lagerwert + Schwund werden seit phase_152 chargen-genau
 * über den tatsächlichen Charge-EK (stock_lots.unit_cost_cents) bewertet
 * (stock_value_totals/shrinkage_*). Kalkulation ≠ Bestandsbewertung — hier
 * NICHT auf Charge-EK umstellen.
 *
 * @param {{purchasePrice,purchaseQty,yieldCleaning,yieldCooking}} ing
 * @returns {number|null} €/Basiseinheit oder null (fehlender/≤0-Einkauf bzw. ≤0-Yield)
 */
export function costPerBase(ing) {
  if (!ing) return null;
  const pp = ing.purchasePrice;
  const pq = ing.purchaseQty;
  if (pp == null || pq == null || !(pp > 0) || !(pq > 0)) return null;
  const yc = ing.yieldCleaning != null ? Number(ing.yieldCleaning) : 1.0;
  const yk = ing.yieldCooking != null ? Number(ing.yieldCooking) : 1.0;
  const yield_ = yc * yk;
  if (!(yield_ > 0)) return null;
  return (Number(pp) / Number(pq)) / yield_;
}

/**
 * Rezeptmenge in Basiseinheiten (g/ml/Stück) — OHNE Stück→Gramm-Umrechnung
 * (Kosten = €/Basiseinheit × Basiseinheiten). Spiegel der Mengenlogik in
 * menu_item_cost/compose_cost.
 * @returns {number|null} null bei fehlender Menge oder unbekannter benannter Einheit
 */
export function qtyInBaseUnits(quantity, unit, namedUnits = []) {
  if (quantity == null || !isFinite(quantity)) return null;
  if (unit == null || unit === 'base') return Number(quantity);
  const nu = (namedUnits || []).find(u => u && String(u.unitName).toLowerCase() === String(unit).toLowerCase());
  if (!nu || nu.gramsOrMl == null) return null;
  return Number(quantity) * Number(nu.gramsOrMl);
}

/** Zeilen-Kosten (€) einer Rezeptzeile, oder null wenn Menge/€ fehlen. */
export function lineCost(qtyBase, cpb) {
  if (qtyBase == null || cpb == null) return null;
  return qtyBase * cpb;
}

/**
 * Aggregiert den Wareneinsatz über Rezeptzeilen — Spiegel von menu_item_cost /
 * compose_cost. Überspringt optionale Zeilen + Zeilen ohne Zutat (wie SQL-WHERE +
 * der JOIN). missing_data, wenn eine einbezogene Zeile keinen €/keine Menge auflöst.
 * @param {Array<{ingredientId,quantity,unit,isOptional}>} recipeItems
 * @param {Object<string,object>} ingredientsById
 * @param {Object<string,Array>} [namedUnitsByIngredient]
 * @returns {{goodsCost:number, missingData:boolean, ingredientCount:number}}
 */
export function aggregateCost(recipeItems, ingredientsById, namedUnitsByIngredient = {}) {
  let total = 0;
  let missing = false;
  let count = 0;
  for (const ri of (Array.isArray(recipeItems) ? recipeItems : [])) {
    if (!ri || ri.isOptional) continue;
    const ing = ingredientsById && ingredientsById[ri.ingredientId];
    if (!ing) continue; // wie SQL-JOIN: fehlende/gelöschte Zutat fällt raus
    count++;
    const qtyBase = qtyInBaseUnits(ri.quantity, ri.unit, namedUnitsByIngredient[ri.ingredientId] || []);
    const cpb = costPerBase(ing);
    const lc = lineCost(qtyBase, cpb);
    if (lc == null) missing = true; else total += lc;
  }
  return { goodsCost: _round(total, 2), missingData: missing, ingredientCount: count };
}

/** Food-Cost-Prozent = goods_cost / Verkaufspreis × 100 (price > 0), sonst null. */
export function foodCostPct(goodsCost, price) {
  if (goodsCost == null || price == null || !(Number(price) > 0)) return null;
  return (Number(goodsCost) / Number(price)) * 100;
}

/** Marge in € = Verkaufspreis − Wareneinsatz. null, wenn eins fehlt. */
export function marginEuro(price, goodsCost) {
  if (price == null || goodsCost == null) return null;
  return Number(price) - Number(goodsCost);
}

/**
 * Ampel-Band für einen Food-Cost-%. Schwellen (Prozentpunkte, tunbar):
 *   pct ≤ target               → 'green'
 *   target < pct ≤ target+5    → 'yellow'
 *   pct > target+5             → 'red'
 *   pct == null                → 'unknown' (Daten fehlen)
 */
export function ampelBand(pct, targetPct, opts = {}) {
  if (pct == null) return 'unknown';
  const t = (targetPct != null && isFinite(Number(targetPct))) ? Number(targetPct) : 30;
  const yellow = (opts && isFinite(Number(opts.yellowBandPp))) ? Number(opts.yellowBandPp) : 5;
  if (pct <= t) return 'green';
  if (pct <= t + yellow) return 'yellow';
  return 'red';
}

/**
 * Ampel-Band inkl. Datenlage. Bei unvollständigen Einkaufsdaten (missingData) ist
 * der berechnete pct nur ein Teilwert → NICHT grün/gelb/rot zeigen, sondern 'unknown'
 * (grau). Sonst wie ampelBand. (S158-Hotfix: missing_data kippte fälschlich auf grün.)
 * @param {{pct, missingData, targetPct, yellowBandPp}} args
 */
export function ampelFor({ pct, missingData, targetPct, yellowBandPp = 5 } = {}) {
  if (missingData) return 'unknown';
  return ampelBand(pct, targetPct, { yellowBandPp });
}

/**
 * S166 — zentrale Ampel-Farben für die ganze ERP-Warenwirtschaft (Food-Cost, Mindest-
 * bestand, MHD-Alarm, Negativ-Markierung). Eine Quelle statt je-View-Duplikate. Die Hex-
 * Werte entsprechen den bisher genutzten (grün/gelb/rot/grau), damit nichts „springt".
 */
export const AMPEL_COLORS = { green: '#2f6b3a', yellow: '#d9a227', red: '#b94a48', unknown: '#9aa0a6' };

/** Hex einer Ampel-Stufe (green|yellow|red|unknown); unbekannt → grau. */
export function ampelColor(band) {
  return AMPEL_COLORS[band] || AMPEL_COLORS.unknown;
}

/** Wirksames Food-Cost-Ziel: Pro-Gericht-Override (meta) vor globalem Wert vor 30. */
export function targetPctFor(meta, globalTarget) {
  const m = meta && meta.foodCostTargetPct;
  if (m != null && isFinite(Number(m)) && Number(m) > 0) return Number(m);
  if (globalTarget != null && isFinite(Number(globalTarget)) && Number(globalTarget) > 0) return Number(globalTarget);
  return 30;
}

/** €-Formatierung (deutsch, 2 NK). null/NaN → „—". */
export function formatEuro(n) {
  if (n == null || !isFinite(Number(n))) return '—';
  return Number(n).toFixed(2).replace('.', ',') + ' €';
}

/** %-Formatierung (0 NK). null/NaN → „—". */
export function formatPct(n) {
  if (n == null || !isFinite(Number(n))) return '—';
  return Math.round(Number(n)) + ' %';
}

/**
 * Bequem-Wrapper für die Anzeige: aus Wareneinsatz + Preis + Ziel die Kennzahlen.
 * @returns {{goodsCost,pct,margin,band,target}}
 */
export function costEvaluation(goodsCost, price, targetPct) {
  const pct = foodCostPct(goodsCost, price);
  return {
    goodsCost,
    pct,
    margin: marginEuro(price, goodsCost),
    band: ampelBand(pct, targetPct),
    target: targetPct
  };
}
